import { Err, isOk, Result } from "@pacote/result"
import useSWR from "swr"
import useSWRMutation from "swr/mutation"
import { z } from "zod"

import {
  ApiFailure,
  BaseApiErrorResponse,
  baseApiResponseSchema,
  UnhandledApiError,
} from "./api-config"
import { baseClientFetcher } from "./base-fetchers"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

type PasswordRecoveryPayload = {
  contact: string
  phonePrefix: string
  username: string // can be username or fiscal code, but not email
}

const passwordRecoveryResponseSchema = baseApiResponseSchema.extend({
  billId: z.number(),
  jwt: z.string(),
  counterRefresh: z.number(),
})

type PasswordRecoveryServiceResponse = z.infer<
  typeof passwordRecoveryResponseSchema
>

type TpasswordRecoveryErrorResponse = {
  type: "SKEBBY" | "ALREADY_SENT" | "GENERIC_CODE_ERROR" | "CODE_ERROR"
  counterRefresh?: number
  errorMessage?: string
}

type PasswordRecoveryFetcherReturn = Result<
  PasswordRecoveryServiceResponse,
  TpasswordRecoveryErrorResponse
>

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/

const passwordRecoveryServiceEndpoint =
  "/gestisci-password-ms/ms/password/recovery"

/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/

const passwordRecoveryFetcher = async (
  key: string,
  { arg }: { arg: PasswordRecoveryPayload },
): Promise<PasswordRecoveryFetcherReturn> => {
  /*--------------------acquire recaptcha token--------------------*/

  const recaptchaResponseToken = await window.grecaptcha.enterprise.execute(
    window.APP_KEY_RE_CAPTCHA,
    { action: "PASSWORD_RESET" },
  )

  /*--------------------make password recovery request--------------------*/

  const responseResult = await baseClientFetcher<
    PasswordRecoveryServiceResponse,
    BaseApiErrorResponse
  >({
    url: key,
    method: "POST",
    body: {
      ...arg,
      recaptchaToken: recaptchaResponseToken,
    },
    successSchemaValidator: passwordRecoveryResponseSchema,
  })

  if (isOk(responseResult)) return responseResult

  const response = responseResult.value

  if (response.status !== 422)
    throw new UnhandledApiError({
      status: ApiFailure.UNPROCESSABLE_ERROR,
      message: `A non 422 status was returned from ${passwordRecoveryServiceEndpoint} service.`,
      originalStatus: response.status,
      data: response.data,
    })

  const errorInfos = response.data.error?.errorInfos

  const nameError =
    errorInfos && (Object.keys(errorInfos || {})[0] as keyof typeof errorInfos)

  if (!errorInfos || !nameError || !errorInfos[nameError]?.length)
    throw new UnhandledApiError({
      status: ApiFailure.UNPROCESSABLE_ERROR,
      message: `Invalid errorInfos response`,
      originalStatus: response.status,
      data: errorInfos,
    })

  if (nameError === "skebby_validation")
    return Err({
      type: "SKEBBY",
    })

  const errorMessage = errorInfos[nameError][0] ?? ""

  if (errorMessage === "err.otp.alreadySent")
    return Err({ type: "ALREADY_SENT" })

  if (!["err.otp.block", "err.out.limit"].includes(errorMessage))
    return Err({
      type: "GENERIC_CODE_ERROR",
      errorMessage,
    })

  const counterRefresh =
    errorMessage === "err.otp.block"
      ? {
          counterRefresh: 4,
        }
      : {}

  return Err({
    type: "CODE_ERROR",
    ...counterRefresh,
    errorMessage:
      errorMessage === "err.otp.block"
        ? "Otp.expiredOtpCode"
        : "Otp.tooManyAttempts",
  })
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

const usePasswordRecoveryMutation = () => {
  const { data } = useSWR<PasswordRecoveryFetcherReturn>(
    passwordRecoveryServiceEndpoint,
  )

  const { trigger, ...rest } = useSWRMutation(
    passwordRecoveryServiceEndpoint,
    passwordRecoveryFetcher,
    { populateCache: true },
  )

  return {
    ...rest,
    data,
    recoverPassword: trigger,
  }
}

export { usePasswordRecoveryMutation }
